import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Crop, FileText, ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr } from './shared';
import { LogoSizePreview } from './LogoSizePreview';

const ACCEPT = 'image/png,image/jpeg,image/webp';
const MAX_BYTES = 2 * 1024 * 1024;

// The logo lives in Mongo, not on a CDN, so the preview is an object URL built
// from the bytes the API returns. `cacheBust` forces a refetch after a change.
export const LogoUploader = ({ hasLogo, onChange, company }) => {
  const { authHeader } = useAuth();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // Replacing a logo leaves `hasLogo` true, so the fetch has to be keyed on
  // something that changes — otherwise the old image stays on screen.
  const [version, setVersion] = useState(0);

  const loadPreview = useCallback(async () => {
    if (!hasLogo) { setPreview(''); return; }
    try {
      const res = await axios.get(`${API}/billing/settings/logo`, {
        headers: authHeader(), responseType: 'blob',
      });
      setPreview((old) => {
        if (old) URL.revokeObjectURL(old);
        return URL.createObjectURL(res.data);
      });
    } catch {
      setPreview('');
    }
  }, [authHeader, hasLogo]);

  useEffect(() => { loadPreview(); }, [loadPreview, version]);

  const upload = async (file) => {
    if (!file) return;
    setErr('');
    if (file.size > MAX_BYTES) {
      setErr('That file is larger than 2 MB. Please use a smaller image.');
      return;
    }
    setBusy(true);
    try {
      const body = new FormData();
      body.append('file', file);
      await axios.post(`${API}/billing/settings/logo`, body, { headers: authHeader() });
      setVersion((v) => v + 1);
      await onChange(true);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Renders a throwaway invoice with the workshop's real settings so they can
  // see exactly how the logo prints, without issuing a document to anyone.
  const previewSample = async () => {
    setBusy(true);
    setErr('');
    // Open the tab synchronously, inside the click, or Safari blocks it as a
    // popup once the render takes longer than the user-activation window.
    const tab = window.open('', '_blank');
    try {
      const res = await axios.get(`${API}/billing/settings/sample-invoice`, {
        headers: authHeader(), responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (tab) tab.location = url;
      else window.location.href = url;      // popup blocked — show it in place
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (tab) tab.close();
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const trim = async () => {
    setBusy(true);
    setErr('');
    try {
      const { data } = await axios.post(`${API}/billing/settings/logo/trim`, {}, { headers: authHeader() });
      setVersion((v) => v + 1);
      toast.success(data.trimmed ? 'Trimmed the empty margins. Your logo now prints bigger.' : 'Your logo has no empty margins to trim.');
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setErr('');
    try {
      await axios.delete(`${API}/billing/settings/logo`, { headers: authHeader() });
      setPreview('');
      setVersion((v) => v + 1);
      await onChange(false);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="logo-uploader">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="h-24 w-44 shrink-0 rounded-lg border border-dashed border-slate-300 bg-slate-50 grid place-items-center overflow-hidden">
          {preview ? (
            <img src={preview} alt="Workshop logo" className="max-h-20 max-w-40 object-contain" data-testid="logo-preview" />
          ) : (
            <span className="text-xs text-slate-400 px-3 text-center">No logo uploaded</span>
          )}
        </div>
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
              data-testid="logo-upload-btn"
            >
              {busy ? <Loader2 className="animate-spin" size={14} /> : <ImagePlus size={14} />}
              {preview ? 'Replace logo' : 'Upload logo'}
            </button>
            <button
              type="button"
              onClick={previewSample}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
              data-testid="preview-sample-btn"
            >
              <FileText size={14} /> Preview sample invoice
            </button>
            {preview && (
              <button
                type="button"
                onClick={trim}
                disabled={busy}
                className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
                data-testid="logo-trim-btn"
                title="Crop the empty space around your logo so it prints bigger"
              >
                <Crop size={14} /> Trim margins
              </button>
            )}
            {preview && (
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50"
                data-testid="logo-remove-btn"
              >
                <Trash2 size={14} /> Remove
              </button>
            )}
          </div>
          <p className="text-xs text-slate-500 max-w-md">
            PNG, JPG or WEBP up to 2 MB. A transparent PNG looks best. Appears on
            estimates, invoices, GST invoices, inspection forms and eWarranty
            certificates — applied to new downloads immediately.
          </p>
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid="logo-file-input"
      />
      <LogoSizePreview version={version} company={company} />
      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="logo-error">{err}</div>}
    </div>
  );
};
